import React from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';

/**
 * Componente Tabs con Headless UI + Tailwind
 * 
 * Reemplaza los Tabs de Materialize
 * 
 * Uso:
 * <Tabs tabs={[
 *   { label: 'Tab 1', content: 'Contenido 1' },
 *   { label: 'Tab 2', content: <Componente />, icon: IconComponent },
 * ]} />
 */
const Tabs = ({
  tabs = [],
  variant = 'default',
  defaultIndex = 0,
  onChange,
  className = '',
}) => {
  // Estilos para diferentes variantes
  const variants = {
    default: {
      list: 'border-b border-gray-200',
      tab: (selected) =>
        `px-4 py-2.5 text-sm font-medium focus:outline-none transition-colors ${
          selected
            ? 'text-blue-600 border-b-2 border-blue-600 -mb-px'
            : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`,
    },
    pills: {
      list: 'bg-gray-100 p-1 rounded-lg',
      tab: (selected) =>
        `px-4 py-2 text-sm font-medium rounded-md focus:outline-none transition-all ${
          selected
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`,
    },
    boxed: {
      list: 'border border-gray-200 rounded-lg p-1 bg-gray-50',
      tab: (selected) =>
        `px-4 py-2 text-sm font-medium rounded-md focus:outline-none transition-all ${
          selected
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-100'
        }`,
    },
  };

  const currentVariant = variants[variant] || variants.default;

  return (
    <TabGroup defaultIndex={defaultIndex} onChange={onChange} className={className}>
      {/* Lista de tabs */}
      <TabList className={`flex space-x-1 ${currentVariant.list}`}>
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            disabled={tab.disabled}
            className={({ selected }) =>
              `${currentVariant.tab(selected)} ${
                tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } inline-flex items-center`
            }
          >
            {tab.icon && <tab.icon className="w-4 h-4 mr-2" />}
            {tab.label}
            {tab.badge && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-600">
                {tab.badge}
              </span>
            )}
          </Tab>
        ))}
      </TabList>

      {/* Paneles de contenido */}
      <TabPanels className="mt-4">
        {tabs.map((tab, index) => (
          <TabPanel
            key={index}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
          >
            {tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

/**
 * Variante: Tabs verticales
 */
export const VerticalTabs = ({
  tabs = [],
  defaultIndex = 0,
  onChange,
  className = '',
}) => {
  return (
    <TabGroup
      defaultIndex={defaultIndex}
      onChange={onChange}
      className={`flex gap-4 ${className}`}
      vertical
    >
      {/* Lista de tabs vertical */}
      <TabList className="flex flex-col space-y-1 w-48 shrink-0">
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            disabled={tab.disabled}
            className={({ selected }) =>
              `px-4 py-2.5 text-sm font-medium text-left rounded-lg focus:outline-none transition-colors ${
                selected
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
              } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`
            }
          >
            <div className="flex items-center">
              {tab.icon && <tab.icon className="w-4 h-4 mr-2" />}
              {tab.label}
            </div>
          </Tab>
        ))}
      </TabList>

      {/* Paneles de contenido */}
      <TabPanels className="flex-1">
        {tabs.map((tab, index) => (
          <TabPanel
            key={index}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
          >
            {tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

export default Tabs;
